import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit2, Eye, Inbox, Loader2, Plus, Search, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { licenseAPI } from '../utils/api';
import LicenseForm from '../components/LicenseForm';
import LicenseViewModal from '../components/LicenseViewModal';
import LicenseCreatedModal from '../components/LicenseCreatedModal';
import LicenseDeleteModal from '../components/LicenseDeleteModal';
import LicenseStatusBadge from '../components/LicenseStatusBadge';
import Pagination from '../components/Pagination';

const Licences = () => {
  const { t } = useTranslation();

  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const limit = 12;

  const [showForm, setShowForm] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [viewingLicense, setViewingLicense] = useState(null);
  const [deletingLicense, setDeletingLicense] = useState(null);
  const [createdLicense, setCreatedLicense] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const searchTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  const fetchLicenses = useCallback(
    async (searchTerm = '', currentPage = page, currentStatus = statusFilter) => {
      try {
        setLoading(true);
        const params = { page: currentPage, limit };
        if (searchTerm) params.search = searchTerm;
        if (currentStatus && currentStatus !== 'all') params.status = currentStatus;
        const response = await licenseAPI.list(params);
        setLicenses(response.data.data || []);
        setTotalPages(response.data.totalPages || 1);
        setTotal(response.data.total || 0);
      } catch (error) {
        toast.error(t('licences.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, statusFilter]
  );

  useEffect(() => {
    fetchLicenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      fetchLicenses(searchQuery, 1, statusFilter);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
    fetchLicenses(searchQuery, 1, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    fetchLicenses(searchQuery, page, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleCreateSubmit = async (data) => {
    setFormSubmitting(true);
    try {
      const response = await licenseAPI.create(data);
      setCreatedLicense(response.data.data);
      setShowForm(false);
      fetchLicenses(searchQuery, 1, statusFilter);
      setPage(1);
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleEditSubmit = async (data) => {
    setFormSubmitting(true);
    try {
      await licenseAPI.update(editingLicense._id, data);
      toast.success(t('licences.form.saveChanges'));
      setEditingLicense(null);
      fetchLicenses(searchQuery, page, statusFilter);
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingLicense) return;
    setDeleteSubmitting(true);
    try {
      await licenseAPI.toggleStatus(deletingLicense._id, false);
      toast.success(t('licences.deleteModal.success'));
      setDeletingLicense(null);
      fetchLicenses(searchQuery, page, statusFilter);
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const STATUS_FILTERS = [
    { value: 'all', label: t('licences.statusFilter.all') },
    { value: 'connected', label: t('licences.statusFilter.connected') },
    { value: 'not_connected', label: t('licences.statusFilter.notConnected') },
    { value: 'expired', label: t('licences.statusFilter.expired') },
    { value: 'inactive', label: t('licences.statusFilter.inactive') },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('licences.title')}</h1>
          <p className="text-gray-500 text-sm mt-1">{t('licences.subtitle')}</p>
        </div>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingLicense(null);
          }}
          className="btn-primary flex items-center gap-2 w-full sm:w-auto justify-center"
        >
          {showForm ? (
            <>
              <X className="w-4 h-4" />
              {t('licences.close')}
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              {t('licences.addNew')}
            </>
          )}
        </button>
      </div>

      {showForm && (
        <div className="accordion-enter">
          <LicenseForm
            mode="create"
            onSubmit={handleCreateSubmit}
            onCancel={() => setShowForm(false)}
            submitting={formSubmitting}
          />
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('licences.table.searchPlaceholder')}
              className="form-input pl-10 w-full"
            />
          </div>
          <div className="inline-flex items-center bg-gray-100 rounded-md p-0.5">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                  statusFilter === f.value ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-600'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-3 text-sm text-gray-500">
          {t('licences.totalCount')}: <strong className="text-gray-900">{total}</strong> {t('licences.license')}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
          <p className="text-gray-500 mt-2">{t('common.loading')}</p>
        </div>
      ) : licenses.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-16 text-center">
          <Inbox className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium text-gray-600">{t('licences.emptyTitle')}</p>
          <p className="text-sm mt-1 text-gray-400">{t('licences.emptyHint')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('licences.table.id')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('licences.table.user')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('licences.table.period')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('licences.table.remainingDays')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('licences.table.status')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('licences.table.operations')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {licenses.map((license) => (
                  <tr key={license._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">{license.displayId}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {license.user ? `${license.user.firstName} ${license.user.lastName}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {t(`licences.period.${license.periodDays}days`)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={license.remainingDays === 0 ? 'text-red-600 font-medium' : 'text-gray-700'}>
                        {license.remainingDays ?? 0} days
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <LicenseStatusBadge status={license.status} />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setViewingLicense(license)}
                          className="text-primary-600 hover:text-primary-800 p-1"
                          title={t('common.view')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingLicense(license);
                            setShowForm(false);
                          }}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title={t('common.edit')}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeletingLicense(license)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title={t('common.delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && licenses.length > 0 && totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      )}

      {editingLicense && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop" onClick={() => !formSubmitting && setEditingLicense(null)}>
          <div className="modal-content bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <LicenseForm
              mode="edit"
              initialValues={editingLicense}
              onSubmit={handleEditSubmit}
              onCancel={() => setEditingLicense(null)}
              submitting={formSubmitting}
            />
          </div>
        </div>
      )}

      <LicenseViewModal license={viewingLicense} onClose={() => setViewingLicense(null)} />
      <LicenseCreatedModal license={createdLicense} onClose={() => setCreatedLicense(null)} />
      <LicenseDeleteModal
        license={deletingLicense}
        onConfirm={handleDeleteConfirm}
        onClose={() => !deleteSubmitting && setDeletingLicense(null)}
        submitting={deleteSubmitting}
      />
    </div>
  );
};

export default Licences;
